import axios from "axios";
import { useState, useEffect } from "react";
import { BACKEND } from "../config/configEnvs";
import SearchIcon from "@mui/icons-material/Search";
import LoadingScreen from "../components/ui/temp-screens/LoadingScreen";
import NoDataScreen from "../components/ui/temp-screens/NoDataScreen";
import PaginationItem from "../components/ui/components/Pagination";
import { toast } from "react-toastify";
import { FaMoneyBillWave } from "react-icons/fa";
import { useSelector } from "react-redux";
import { months, years } from "../utils/rawData/dateValues";
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';
import DrawerCotizacionCalendarioPagosDetails from "../components/calendarioPagos/DrawerCotizacionCalendarioPagosDetails";
import DetailsButton from "../components/ui/components/DetailsButton";
import NormalizeText from "../utils/normalizeValues/NormalizeText";
import ServicesCategories from "../utils/plainValues/ServicesCategories";
import Tooltip from "../components/ui/ToolTip";
import { FaCalendarDays } from "react-icons/fa6";
import Selector from "../components/ui/components/Selector";
import SearchBar from "../components/ui/components/SearchBar";
import { Limits10_50 } from "../utils/plainValues/DefaultLimits";
import Timeline from "../components/ui/Timeline";
import "../components/ui/css/AutoScrollCarousel.css";

const IngresosMensuales = () => {
  
  const user = useSelector((state) => state.auth?.user); // Usuario logeado
  const isAdmin = user.area.toUpperCase() === "ADMINISTRACION" && user.puesto.toUpperCase() !== "ASISTENTE"

  const [loading, setLoading] = useState(false)
  
  const [record, setRecord] = useState([]);

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);

  const [services, setServices] = useState([]);
  const [categoriasView, setCategoriasView] = useState('hidden'); // 'hidden' | 'chart' | 'bars' | 'table'

  // Obtener categorías únicas y calcular montos por categoría
  const categorias = ServicesCategories.map((c) => c.value);

  // Calcular montos por categoría
  const montosPorCategoria = {};
  categorias.forEach(categoria => {
    montosPorCategoria[categoria] = record.reduce((total, cliente) => {
      const montoCategoria = cliente.pagos.reduce((acc, pago) => {
        const montoServicios = pago.montosServicios?.reduce((sum, monto) => {
          const servicio = services.find(s => s.id === monto?.servicioId?.servicio);
          return sum + (servicio?.category === categoria ? (monto.monto || 0) : 0);
        }, 0) || 0;
        return acc + montoServicios;
      }, 0);
      return total + montoCategoria;
    }, 0);
  });

  // Filtrar categorías con montos mayores a 0
  const categoriasConMonto = categorias.filter(categoria => montosPorCategoria[categoria] > 0);

  // Total de montos por categorías visibles (base para %)
  const totalCategoriasMonto = categoriasConMonto.reduce((acc, categoria) => acc + (montosPorCategoria[categoria] || 0), 0);

  // Data para el gráfico de pie por categoría
  const pieDataCategorias = categoriasConMonto.map((categoria) => ({
    id: categoria,
    label: categoria,
    value: montosPorCategoria[categoria],
    percentage: totalCategoriasMonto > 0 ? (montosPorCategoria[categoria] / totalCategoriasMonto) : 0
  }));

  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  // Construir matriz mensual por categoría (solo si mes = "Todos")
  const monthlyCategoryMatrix = months.map((m, index) => {
    const monthIndex = index + 1;
    const totalsByCategory = categoriasConMonto.reduce((acc, categoria) => {
      acc[categoria] = 0;
      return acc;
    }, {});

    record.forEach((cliente) => {
      cliente.pagos.forEach((pago) => {
        const dateStr = pago?.fechaLimite;
        if (!dateStr) return;
        const dateObj = new Date(dateStr);
        if (isNaN(dateObj)) return;
        if (dateObj.getFullYear() !== Number(year)) return;
        if ((dateObj.getMonth() + 1) !== monthIndex) return;

        pago.montosServicios?.forEach((monto) => {
          const servicio = services.find(s => s.id === monto?.servicioId?.servicio);
          const categoria = servicio?.category;
          if (categoria && categoriasConMonto.includes(categoria)) {
            totalsByCategory[categoria] += (monto.monto || 0);
          }
        });
      });
    });

    const totalMonth = Object.values(totalsByCategory).reduce((a, b) => a + b, 0);
    const percentagesByCategory = categoriasConMonto.reduce((acc, categoria) => {
      acc[categoria] = totalMonth > 0 ? (totalsByCategory[categoria] / totalMonth) : 0;
      return acc;
    }, {});

    return {
      monthIndex,
      monthLabel: m.label,
      totalsByCategory,
      percentagesByCategory,
      totalMonth,
    };
  }).filter(row => row.totalMonth > 0);

  // Data para gráfico de barras apiladas por mes y categoría
  const barDataCategoriasMensual = monthlyCategoryMatrix.map((row) => ({
    month: row.monthLabel,
    ...row.totalsByCategory,
  }));

  // Preparar datos para la timeline de pagos
  const timelineData = record.flatMap((cliente) => 
    cliente.pagos
      .map((pago) => {
        const fecha = pago.fechaPago || pago.fechaLimite;
        if (!fecha) return null;

        // Sumar 5 horas a la fecha
        const fechaConHora = new Date(fecha);
        fechaConHora.setHours(fechaConHora.getHours() + 5);

        // Obtener la categoría principal del pago (la primera que encuentre)
        let categoriaPrincipal = null;
        if (pago.montosServicios && pago.montosServicios.length > 0) {
          const primerServicio = services.find(s => s.id === pago.montosServicios[0]?.servicioId?.servicio);
          categoriaPrincipal = primerServicio?.category || null;
        }

        return {
          fecha: fechaConHora.toISOString(),
          cliente: cliente.nombreComercial,
          monto: pago.precio || 0,
          estado: pago.pagado ? 'Pagado' : 'Pendiente',
          verificado: pago.pagoVerificado || false,
          cotizacion: pago.cotizacion,
          categoria: categoriaPrincipal,
        };
      })
      .filter(Boolean)
  )
  .sort((a, b) => {
    // Ordenar por fecha (más reciente primero)
    const dateA = new Date(a.fecha);
    const dateB = new Date(b.fecha);
    return dateB - dateA;
  });

  const [brandName, setBrandName] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("all");

  const [paises, setPaises] = useState([]);
  const [currencyExchanges, setCurrencyExchanges] = useState({})

  const getRecord = async () => {
    try {
      setLoading(true);
      let url = `${BACKEND}/api/getMonthlyIncomes?userId=${user.id}`;

      if (brandName) {
        url += `&brandName=${brandName}`;
      }
      if (month) {
        url += `&month=${month}`;
      }
      if (year) {
        url += `&year=${year}`;
      }
      const res = await axios.get(url);

      // Filter by payment status
      let filteredClients = res.data.clients;
      if (paymentStatus !== "all") {
        filteredClients = filteredClients.filter(client => {
          const nonPayed = client.pagos.filter((p) => p.pagado === false).reduce((acc, p) => acc + p.precio, 0);
          return paymentStatus === "paid" ? nonPayed === 0 : nonPayed > 0;
        });
      }

      setRecord(filteredClients);

    } catch (error) {
      toast.error("Error al obtener los datos");
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  const getServices = async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/getServices`);
      setServices(r.data.services);
    } catch (error) {
      console.log(error);
    }
  }

  const getPaises = async () => {
    try {
      const response = await axios.get(`${BACKEND}/api/paises?limit=0`);
      setPaises(response.data.docs);
    } catch (error) {
      console.error(error);
    }
  }

  const getCurrencyExchange = async () => {
    try {
      const response = await axios.get(`${BACKEND}/api/tiposCambio?limit=0`)
      const currencyExchanges = response.data.docs.reduce((acc, item) => {
        acc[item.moneda] = item.tipoCambio
        return acc
      }, {})
      setCurrencyExchanges(currencyExchanges)
    } catch (error) {
      return false
    }
  }

  useEffect(() => {
    getRecord();
    getServices()
    getPaises()
    getCurrencyExchange()
  }, []);

  useEffect(() => {
    getRecord();
  }, [month, year, limit, page, paymentStatus]);

  useEffect(() => {
    if (brandName === "") {
      getRecord();
      setPage(1);
    }
  }, [brandName]);

  // Preprocesar los montos por servicio para cada cliente
  const montosPorServicioPorCliente = record.map((c) => {
    const montosPorServicio = {};
    let igvTotal = 0;

    services.forEach((s) => {
      const totalMonto = c.pagos.reduce((acc, p) => {
        const montoServicio = p.montosServicios
          ?.filter((m) => m?.servicioId?.servicio === s.id)
          ?.reduce((sum, m) => {
            // Suma el monto normal
            let monto = m.monto || 0;
            // Si tiene IGV, suma el 18% al igvTotal
            if (m.igv === "si") {
              igvTotal += monto * 0.18;
            }
            return sum + monto;
          }, 0) || 0;
        return acc + montoServicio;
      }, 0);
      montosPorServicio[s.id] = totalMonto;
    });

    return {
      clienteId: c.id,
      montosPorServicio,
      igvTotal,
    };
  });

  // Totales generales
  const totalPagado = record.reduce((acc, c) => acc + c.pagos.filter((p) => p.pagado === true).reduce((a, p) => a + p.precio, 0), 0);
  const totalDebe = record.reduce((acc, c) => acc + c.pagos.filter((p) => p.pagado === false).reduce((a, p) => a + p.precio, 0), 0);
  const totalIGV = montosPorServicioPorCliente.reduce((acc, c) => acc + c.igvTotal, 0);
  //const totalPagos = record.reduce((acc, c, idx) => acc + c.pagos.reduce((a, p) => a + p.precio, 0) + montosPorServicioPorCliente[idx]?.igvTotal, 0);
  //const totalPagos = record.reduce((acc, c) => acc + c.pagos.reduce((a, p) => a + p.precio, 0), 0);
  const totalPayments = record.reduce((acc, c) => acc + c.pagos.reduce((a, p) => a + p.precio, 0), 0);
  const totalWithoutIGV = record.reduce((acc, c, idx) => acc + c.pagos.reduce((a, p) => a + p.precio, 0) - montosPorServicioPorCliente[idx]?.igvTotal, 0);
  // Totales por servicio
  const totalesPorServicio = services.map(s =>
    record.reduce((acc, c, idx) => acc + (montosPorServicioPorCliente[idx]?.montosPorServicio[s.id] || 0), 0)
  );

  const totalPagos = record.reduce((acc, c) => acc + c.pagos.length, 0);

  const totalPagosVerificados = record.reduce((acc, c) => acc + c.pagos.filter((p) => p.pagoVerificado).length, 0);
  const totalPagosNoVerificados = record.reduce((acc, c) => acc + c.pagos.filter((p) => !p.pagoVerificado).length, 0);
  const totalMonPagosVerificados = record.reduce((acc, c) => acc + c.pagos.filter((p) => p.pagado && p.pagoVerificado).reduce((a, p) => a + p.precio, 0), 0);
  const totalMonPagosNoVerificados = record.reduce((acc, c) => acc + c.pagos.filter((p) => p.pagado && !p.pagoVerificado).reduce((a, p) => a + p.precio, 0), 0);

  // Solo mostrar servicios con total > 0
  const serviciosVisibles = services.filter((s, i) => totalesPorServicio[i] > 0);

  // Sumar ingresos por banco con desglose de pagado, debe y total
  const ingresosPorBanco = {};
  record.forEach((c) => {
    c.pagos.forEach((p) => {
      const cuenta = p.cuenta || "N/A";
      if (!ingresosPorBanco[cuenta]) {
        ingresosPorBanco[cuenta] = {
          pagado: 0,
          debe: 0,
          total: 0
        };
      }
      
      const monto = p.precio || 0;
      ingresosPorBanco[cuenta].total += monto;
      
      if (p.pagado) {
        ingresosPorBanco[cuenta].pagado += monto;
      } else {
        ingresosPorBanco[cuenta].debe += monto;
      }
    });
  });

  // Convierte a array para mapear en el render
  const accounts = Object.entries(ingresosPorBanco).map(([name, amounts]) => ({
    name,
    pagado: amounts.pagado,
    debe: amounts.debe,
    total: amounts.total,
  }));

  const [openDrawer, setOpenDrawer] = useState(false);
  const [dataDrawer, setDataDrawer] = useState(null);

  
  const handleOpenDrawer = async (id) => {
    try {
      const response = await axios.get(`${BACKEND}/api/cotizaciones/${id}`)
      setDataDrawer(response.data)
      setOpenDrawer(true)
    } catch (error) {
      toast.error("Error al obtener la cotización")
    }
  } 

  const globalDefs = [
    {
      id: 'dots',
      type: 'patternDots',
      background: 'inherit',
      color: 'rgba(255, 255, 255, 0.3)',
      size: 4,
      padding: 1,
      stagger: true
    },
    {
      id: 'lines',
      type: 'patternLines',
      background: 'inherit',
      color: 'rgba(255, 255, 255, 0.3)',
      rotation: -45,
      lineWidth: 6,
      spacing: 10
    },
    {
      id: 'squares',
      type: 'patternSquares',
      background: 'inherit',
      color: 'rgba(255, 255, 255, 0.3)',
      rotation: 0,
      lineWidth: 50,
      spacing: 50
    },
    {
      id: 'crossHatch',
      type: 'patternLines',
      background: 'inherit',
      color: 'rgba(255,255,255,0.35)',
      rotation: 45,
      lineWidth: 4,
      spacing: 8
    },
    {
      id: 'gradientBlue',
      type: 'linearGradient',
      colors: [
        { offset: 0, color: '#60a5fa' },
        { offset: 100, color: '#1d4ed8' }
      ]
    }
  ]

  const chartTheme = {
    axis: {
      ticks: {
        text: {
          fill: "#666666",
          fontSize: 12
        }
      },
      legend: {
        text: {
          fill: "#666666",
          fontSize: 12
        }
      }
    },
    legends: {
      text: {
        fill: "#666666",
        fontSize: 12
      }
    }
  };

  return (
    <div className="flex flex-col h-screen w-full">

      <DrawerCotizacionCalendarioPagosDetails
        onClose={() => setOpenDrawer(false)}
        open={openDrawer}
        dataDrawer={dataDrawer}
        refetch={getRecord}
        isAdmin={isAdmin}
        paises={paises}
        currencyExchanges={currencyExchanges}
      />

      {/* NavBar */}
      <div className="flex flex-row px-4 py-2 bg-gray-100">
        <div className="flex flex-row w-full items-center gap-4 justify-between overflow-x-auto overflow-y-hidden px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          
          <div className="flex items-center gap-4 py-4">
            <FaMoneyBillWave className="text-zinc-700 text-2xl" />
            <h2 className="text-2xl font-bold text-zinc-700">Ingresos mensuales</h2>
          </div>

          <div className="flex flex-row gap-4 items-center justify-center">
            <Selector
              title="Límite"
              value={limit}
              setter={setLimit}
              options={Limits10_50}
              disabled={loading}
              width="w-[5rem]"
            />

            <SearchBar
              value={brandName}
              setter={setBrandName}
              pageSetter={setPage}
              getter={getRecord}
              placeholder="Buscar marca..."
              text="Buscar"
              disabled={loading}
              icon={<SearchIcon />}
            />
          </div>

        </div>
      </div>

      {/* Ingresos por categoría */}
      <div className="flex flex-col bg-gray-100 mt-0.5">
        <div className="flex sticky top-0 bg-gray-100 z-10 flex-row py-2">
          <div className="flex items-center px-4 gap-2">
            <Selector
              title="Vista"
              value={categoriasView}
              setter={(val) => { setCategoriasView(val); if (val === "bars" || val === "table") { setMonth(""); } }}
              options={[{label: "Ocultar", value: "hidden"}, {label: "Gráfico categoría", value: "chart"}, {label: "Barras mes/categoría", value: "bars"}, {label: "Tabla mes/categoría", value: "table"}, {label: "Línea de tiempo", value: "timeLine"}]}
            />
          </div>
          <div className="flex flex-row w-full items-center gap-4 justify-between overflow-hidden px-8 relative">
            {categoriasConMonto.length > 3 ? (
              <div 
                className="auto-scroll-wrapper"
                style={{ '--auto-scroll-duration': `${Math.max(20, categoriasConMonto.length * 3)}s` }}
              >
                <div className="auto-scroll-container">
                    {/* Primera copia */}
                    {categoriasConMonto.map((c, i) => (
                      <div key={`first-${i}`} className="flex flex-row text-xs text-gray-700 items-center justify-center gap-2 flex-shrink-0 px-2">
                        <span className="whitespace-nowrap">{c}:</span>
                        <span className="whitespace-nowrap text-black font-bold">S/. {montosPorCategoria[c].toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className="whitespace-nowrap text-black font-bold">{totalCategoriasMonto > 0 ? ((montosPorCategoria[c] / totalCategoriasMonto) * 100).toFixed(2) : '0.00'}%</span>
                      </div>
                    ))}
                    {/* Segunda copia para efecto infinito */}
                    {categoriasConMonto.map((c, i) => (
                      <div key={`second-${i}`} className="flex flex-row text-xs text-gray-700 items-center justify-center gap-2 flex-shrink-0 px-2">
                        <span className="whitespace-nowrap">{c}:</span>
                        <span className="whitespace-nowrap text-black font-bold">S/. {montosPorCategoria[c].toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className="whitespace-nowrap text-black font-bold">{totalCategoriasMonto > 0 ? ((montosPorCategoria[c] / totalCategoriasMonto) * 100).toFixed(2) : '0.00'}%</span>
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-row gap-4 items-center justify-center px-6 w-full">
                {categoriasConMonto.map((c, i) => (
                  <div key={i} className="flex flex-row text-xs text-gray-700 items-center justify-center gap-2">
                    <span className="whitespace-nowrap">{c}:</span>
                    <span className="whitespace-nowrap text-black font-bold">S/. {montosPorCategoria[c].toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <span className="whitespace-nowrap text-black font-bold">{totalCategoriasMonto > 0 ? ((montosPorCategoria[c] / totalCategoriasMonto) * 100).toFixed(2) : '0.00'}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {categoriasView === 'chart' && pieDataCategorias.length > 0 && (
          <div className="w-full bg-white">
            <div className="h-80 px-16 py-4">
              <ResponsivePie
                data={pieDataCategorias}
                margin={{ top: 20, right: 250, bottom: 40, left: 60 }}
                innerRadius={0.5}
                padAngle={3.5}
                cornerRadius={3}
                activeOuterRadiusOffset={8}
                colors={{ scheme: 'paired' }}
                theme={chartTheme}
                defs={globalDefs}
                fill={[
                  { match: { id: 'ADS' }, id: 'dots' },
                  { match: { id: 'Web' }, id: 'lines' },
                  { match: { id: 'SEO' }, id: 'squares' },
                  { match: { id: 'Software' }, id: 'crossHatch' },
                  { match: { id: 'SMM' }, id: 'squares' },
                ]}
                borderWidth={1}
                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                arcLinkLabelsSkipAngle={1}
                arcLinkLabelsTextColor="#333333"
                arcLinkLabelsThickness={1}
                arcLinkLabelsColor={{ from: 'color' }}
                arcLabelsSkipAngle={1}
                arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                arcLabel={(d) => `${(d.data.percentage * 100).toFixed(1)}%`}
                tooltip={({ datum }) => (
                  <div className="px-2 py-1 bg-white text-xs text-gray-700 border border-gray-200 rounded shadow">
                    <span className="font-bold">{datum.id}</span>: S/. {Number(datum.value).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                )}
                legends={[
                  {
                    data: categoriasConMonto.map((categoria) => ({
                      id: categoria,
                      label: `${categoria}: S/. ${montosPorCategoria[categoria].toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${totalCategoriasMonto > 0 ? ((montosPorCategoria[categoria] / totalCategoriasMonto) * 100).toFixed(1) : '0.0'}%)`,
                      // Ensure that each legend item uses the same color as the bars
                      color: 'inherit'
                    })),
                    anchor: 'right',
                    direction: 'column',
                    justify: false,
                    translateX: 100,
                    translateY: 0,
                    itemsSpacing: 4,
                    itemWidth: 100,
                    itemHeight: 18,
                    itemTextColor: '#555',
                    itemDirection: 'left-to-right',
                    itemOpacity: 1,
                    symbolSize: 12,
                    symbolShape: 'circle'
                  }
                ]}
              />
            </div>
          </div>
        )}
        {categoriasView === 'bars' && month === "" && monthlyCategoryMatrix.length > 0 && (
          loading ? (
            <div className="h-96 bg-white">
              <LoadingScreen />
            </div>
          ) : (
            <div className="w-full bg-white">
              <div className="h-96 px-4 py-4">
              <ResponsiveBar
                data={barDataCategoriasMensual}
                keys={categoriasConMonto}
                indexBy="month"
                margin={{ top: 20, right: 250, bottom: 50, left: 70 }}
                padding={0.2}
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={{ scheme: 'paired' }} // Use a color scheme that ensures consistency
                theme={chartTheme}
                defs={globalDefs}
                axisTop={null}
                axisRight={null}
                fill={[
                  { match: { id: 'ADS' }, id: 'dots' },
                  { match: { id: 'Web' }, id: 'lines' },
                  { match: { id: 'SEO' }, id: 'squares' },
                  { match: { id: 'Software' }, id: 'crossHatch' },
                  { match: { id: 'SMM' }, id: 'squares' },
                ]}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Mes',
                  legendPosition: 'middle',
                  legendOffset: 36,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Ingresos (S/.)',
                  legendPosition: 'middle',
                  legendOffset: -50,
                }}
                enableLabel={true}
                labelSkipHeight={8}
                label={(d) => `${String(d.id)}`}
                tooltip={({ id, value, indexValue, color }) => (
                  <div className="px-2 py-1 bg-white text-xs text-gray-700 border border-gray-200 rounded shadow" style={{ borderColor: color }}>
                    <span className="font-bold">{String(id)}</span> · {String(indexValue)}: S/. {Number(value).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                )}
                legends={[
                  {
                    // dataFrom: 'keys', // data
                    data: categoriasConMonto.map((categoria) => ({
                      id: categoria,
                      label: `${categoria}: S/. ${montosPorCategoria[categoria].toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${totalCategoriasMonto > 0 ? ((montosPorCategoria[categoria] / totalCategoriasMonto) * 100).toFixed(1) : '0.0'}%)`,
                      // Ensure that each legend item uses the same color as the bars
                      color: 'inherit'
                    })),
                    anchor: 'right',
                    direction: 'column',
                    justify: false,
                    translateX: 240,
                    translateY: 0,
                    itemsSpacing: 6,
                    itemWidth: 200,
                    itemHeight: 18,
                    itemDirection: 'left-to-right',
                    symbolSize: 0,
                    symbolShape: 'circle',
                  },
                ]}
                role="application"
                ariaLabel="Ingresos por categoría y mes"
                barAriaLabel={(e) => `${e.id}: ${e.formattedValue} en mes: ${e.indexValue}`}
                groupMode="stacked"
              />
              </div>
            </div>
          )
        )}
        {categoriasView === 'table' && month === "" && monthlyCategoryMatrix.length > 0 && (
          loading ? (
            <div className="h-96 bg-white">
              <LoadingScreen />
            </div>
          ) : (
            <div className="w-full bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-2 py-2 text-left border-b border-gray-200 sticky left-0 bg-gray-50">Mes</th>
                      {categoriasConMonto.map((c) => (
                        <th key={c} className="px-2 py-2 text-center border-b border-gray-200">{c}</th>
                      ))}
                    </tr>
                    {/* Fila de totales */}
                    <tr className="bg-gray-100 border-b border-gray-300">
                      <td className="px-2 py-2 text-left font-bold sticky left-0 bg-gray-100 border-r border-gray-300">Total</td>
                      {categoriasConMonto.map((c) => {
                        const total = montosPorCategoria[c];
                        const percentage = totalCategoriasMonto > 0 ? (total / totalCategoriasMonto) : 0;
                        return (
                          <td key={c} className="px-2 py-2 text-center border-r border-gray-200">
                            <div className="flex flex-col items-center">
                              <span className="font-bold">{(percentage * 100).toFixed(1)}%</span>
                              <span className="text-gray-600 font-semibold">S/. {total.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyCategoryMatrix.map((row) => (
                      <tr key={row.monthIndex} className="hover:bg-gray-50">
                        <td className="px-2 py-2 text-left border-b border-gray-100 sticky left-0 bg-white">{row.monthLabel}</td>
                        {categoriasConMonto.map((c) => {
                          const total = row.totalsByCategory[c];
                          const percentage = row.percentagesByCategory[c];
                          return (
                          <td key={c} className="px-2 py-2 text-center border-b border-gray-100">
                            <div className="flex flex-col items-center">
                              <span className={percentage === 0 ? "text-gray-200" : "font-bold"}>{(percentage * 100).toFixed(1)}%</span>
                              <span className={total === 0 ? "text-gray-200" : "text-gray-600"}>S/. {total.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </td>
                        )})}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}
        {categoriasView === 'timeLine' && timelineData.length > 0 && (
          loading ? (
            <div className="h-96 bg-white">
              <LoadingScreen />
            </div>
          ) : (
            <div className="w-full bg-white">
              <div className="py-8">
                <Timeline 
                  events={timelineData} 
                  onEventClick={handleOpenDrawer}
                />
              </div>
            </div>
          )
        )}
      </div>

      {/* Ingresos por cuenta */}
      <div className="flex bg-gray-100 mt-0.5 py-2 justify-between items-center w-full gap-10">
        <div className="flex flex-row w-full items-center gap-4 justify-between overflow-hidden px-16 relative">
          {accounts.length > 3 ? (
            <div 
              className="auto-scroll-wrapper"
              style={{ '--auto-scroll-duration': `${Math.max(80, accounts.length * 3)}s` }}
            >
              <div className="auto-scroll-container">
                {/* Primera copia */}
                {accounts.map((a, i) => (
                  <div key={`first-${i}`} className="flex flex-row text-xs text-gray-700 items-center justify-center gap-2 flex-shrink-0 px-2">
                    <span className="font-bold whitespace-nowrap">{a.name}:</span>
                    <span className="text-black whitespace-nowrap text-blue-500">Pagado: S/. {a.pagado.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <span className="text-black whitespace-nowrap text-red-500">Debe: S/. {a.debe.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <span className="text-black whitespace-nowrap text-gray-500">Total: S/. {a.total.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                ))}
                {/* Segunda copia para efecto infinito */}
                {accounts.map((a, i) => (
                  <div key={`second-${i}`} className="flex flex-row text-xs text-gray-700 items-center justify-center gap-2 flex-shrink-0 px-2">
                    <span className="font-bold whitespace-nowrap">{a.name}:</span>
                    <span className="text-black whitespace-nowrap text-blue-500">Pagado: S/. {a.pagado.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <span className="text-black whitespace-nowrap text-red-500">Debe: S/. {a.debe.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <span className="text-black whitespace-nowrap text-gray-500">Total: S/. {a.total.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-row gap-4 items-center justify-center px-6 w-full">
              {accounts.map((a, i) => (
                <div key={i} className="flex flex-row text-xs text-gray-700 items-center justify-center gap-2">
                  <span className="font-bold whitespace-nowrap">{a.name}:</span>
                  <span className="text-black whitespace-nowrap text-blue-500">Pagado: S/. {a.pagado.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="text-black whitespace-nowrap text-red-500">Debe: S/. {a.debe.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="text-black whitespace-nowrap text-gray-500">Total: S/. {a.total.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="flex bg-gray-100 mt-0.5 mb-0.5 justify-between items-center w-full gap-10">
        <div className="flex flex-row w-full items-center px-16 gap-4 justify-between overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">

          <div className="flex flex-row text-xs text-gray-700 items-center justify-center gap-4">
            <span className="whitespace-nowrap">Estado de pago:</span>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="text-gray-700 hover:cursor-pointer text-xs bg-white border border-gray-300 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent py-[6px] pr-10"
            >
              <option value="all">Todos</option>
              <option value="paid">Pagado</option>
              <option value="unpaid">Debe</option>
            </select>
          </div>

          <div className="flex flex-row text-xs text-gray-700 items-center justify-center gap-4">
            <span className="whitespace-nowrap">Mes:</span>
            <select
              value={month}
              onChange={(e) => {
                setMonth(e.target.value)
                if (e.target.value !== "" && (categoriasView === "bars" || categoriasView === "table")) {
                  setCategoriasView("hidden")
                }
              }}
              className="text-gray-700 hover:cursor-pointer text-xs bg-white border border-gray-300 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent py-[6px] pr-10"
            >
              <option value={""}>Todos</option>
              {months.map((month, index) => (
                <option key={index} value={index + 1}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-row text-xs text-gray-700 items-center justify-center gap-4">
            <span>Año:</span>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="text-gray-700 hover:cursor-pointer text-xs bg-white border border-gray-300 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent py-[6px] pr-10"
            >
              {years.map((year, index) => (
                <option key={index} value={year.value}>
                  {year.label}
                </option>
              ))}
            </select>
          </div>

        </div>
      </div>
  
      {/* Body */}
      <div className="flex-grow overflow-y-auto">
        {loading ? (
          <LoadingScreen />
        ) : (
          record.length === 0 ? (
            <NoDataScreen />
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-200 z-50">
                <tr className="text-gray-700 text-center text-xs font-bold z-50">
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 sticky left-0 text-white z-50">
                    Cotización(es)
                  </th>
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 sticky left-0 text-white z-50">
                    Cliente
                  </th>
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                    Encargado
                  </th>
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                    Cantidad de pagos
                  </th>
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                    Fechas de pago
                  </th>
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                    Adelantos de pagos verificados
                  </th>
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                    Adelantos de pagos no verificados
                  </th>
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                    Condición de pago
                  </th>
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                    Adelantó
                  </th>
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                    Debe
                  </th>
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                    Total
                  </th>
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                    Total sin IGV
                  </th>
                  <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                    IGV
                  </th>
                  {serviciosVisibles.map((s, i) => (
                    <th key={i} className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                      {s.name}
                    </th>
                  ))}
                </tr>
                {/* Fila de totales */}
                <tr className="bg-gray-100 font-bold text-xs text-center">
                  <td className="py-2" colSpan={3}>Totales</td>
                  <td className={`text-black py-2 ${totalPagos > 0 ? "text-black" : "text-gray-200"}`}>
                    {totalPagos > 0 ? totalPagos : "-"}
                  </td>
                  <td className="text-black py-2">
                  </td>
                  <td className={`text-black whitespace-nowrap px-2 py-2 ${totalPagosVerificados > 0 ? "text-black" : "text-gray-200"}`}>
                    {totalPagosVerificados > 0 ? (totalPagosVerificados + " pago(s) - S/. " + totalMonPagosVerificados.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : "-"}
                  </td>
                  <td className={`text-black whitespace-nowrap px-2 py-2 ${totalPagosNoVerificados > 0 ? "text-black" : "text-gray-200"}`}>
                    {totalPagosNoVerificados > 0 ? (totalPagosNoVerificados + " pago(s) - S/. " + totalMonPagosNoVerificados.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : "-"}
                  </td>
                  <td className="text-black py-2">
                  </td>
                  <td className="text-blue-600 py-2">
                    {totalPagado > 0 ? "S/. " + totalPagado.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                  </td>
                  <td className="text-red-600 py-2">
                    {totalDebe > 0 ? "S/. " + totalDebe.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                  </td>
                  <td className="text-black py-2">
                    {totalPayments > 0 ? "S/. " + totalPayments.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                  </td>
                  <td className="text-black py-2">
                    {totalWithoutIGV > 0 ? "S/. " + totalWithoutIGV.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                  </td>
                  <td className="text-black py-2">
                    {totalIGV > 0 ? "S/. " + totalIGV.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                  </td>
                  {serviciosVisibles.map((s) => {
                    const idx = services.findIndex(serv => serv.id === s.id);
                    return (
                      <td key={s.id} className="text-black py-2">
                        {totalesPorServicio[idx] > 0 ? "S/. " + totalesPorServicio[idx].toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                      </td>
                    );
                  })}
                </tr>
              </thead>
              <tbody>

                {record.map((c, idx) => {

                  const nonPayed = c.pagos.filter((p) => p.pagado === false).reduce((acc, p) => acc + p.precio, 0);
                  const payed = c.pagos.filter((p) => p.pagado === true).reduce((acc, p) => acc + p.precio, 0);

                  const owes = nonPayed > 0 ? true : false;

                  const payments = c.pagos.length;
                  
                  // Obtener los montos por servicio ya calculados
                  const montosPorServicio = montosPorServicioPorCliente[idx]?.montosPorServicio || {};
                  const igvTotal = montosPorServicioPorCliente[idx]?.igvTotal || 0;

                  const totalWithoutIGV = c.pagos.reduce((acc, p) => acc + p.precio, 0) - igvTotal;

                  const totalPayments = c.pagos.reduce((acc, p) => acc + p.precio, 0);

                  const quotes = [...new Set(c.pagos?.map((p) => p.cotizacion))];

                  const verifyPayments = c.pagos.filter((p) => p.pagoVerificado === true);
                  const verifyNonPayments = c.pagos.filter((p) => p.pagado && !p.pagoVerificado);

                  const verifyAmountPayments = c.pagos.filter((p) => p.pagoVerificado === true).reduce((acc, p) => acc + p.precio, 0);
                  const verifyAmountNonPayments = c.pagos.filter((p) => p.pagado && !p.pagoVerificado).reduce((acc, p) => acc + p.precio, 0);

                  return (
                    <tr key={c.id} className="text-xs py-1 w-full hover:bg-gray-100 transition-all duration-100 z-10">
                      <td className="px-4 py-2 whitespace-nowrap border border-gray-300 sticky left-0 z-10 bg-white"> 
                        <div className="flex flex-row items-center justify-center gap-2">
                          {quotes.map((q, i) => (
                            <DetailsButton
                              key={i}
                              content={q}
                              action={() => handleOpenDrawer(q) }
                            />
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2 border border-gray-300 sticky left-0 z-10 bg-white"> 
                        <div className="flex flex-row gap-2 items-center justify-between">
                          <span className="whitespace-nowrap truncate max-w-[12rem]" title={c.nombreComercial}>{c.nombreComercial}</span>
                          {c.pais && (
                            <Tooltip content={paises.find(p => NormalizeText(p.name) === NormalizeText(c.pais))?.name || "País no encontrado"}>
                              <div className="w-4 h-4 z-10">
                                <img src={paises.find(p => NormalizeText(p.name) === NormalizeText(c.pais))?.bandera?.url} alt="flag" className="w-4 h-4" />
                              </div>
                            </Tooltip>
                          )}  
                        </div>
                      </td>
                      <td className="px-4 py-2 border border-gray-300 whitespace-nowrap truncate max-w-[10rem]" title={c.encargadoProyecto || c.nombreGerente}>
                        {c.encargadoProyecto || c.nombreGerente}
                      </td>
                      <td className={`px-4 py-2 whitespace-nowrap text-center border border-gray-300 font-bold`}>
                        {payments}
                      </td>
                      <td className={`px-4 py-2 whitespace-nowrap text-center border border-gray-300 font-bold`}>
                        {(() => {
                          const formatDate = (dateString) => {
                            if (!dateString) return null;
                            const date = new Date(dateString);
                            if (isNaN(date.getTime())) return null;
                            const day = String(date.getDate()).padStart(2, '0');
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const year = date.getFullYear();
                            return `${day}/${month}/${year}`;
                          };

                          const fechasPago = c.pagos
                            .map((p) => {
                              const fecha = p.fechaPago || p.fechaLimite;
                              const fechaFormateada = formatDate(fecha);
                              if (!fechaFormateada) return null;
                              
                              const monto = p.precio || 0;
                              const estado = p.pagado ? "Pagado" : "Pendiente";
                              
                              return {
                                fecha: fechaFormateada,
                                monto: monto,
                                estado: estado
                              };
                            })
                            .filter(Boolean)
                            .sort((a, b) => {
                              // Ordenar por fecha (más reciente primero)
                              const dateA = new Date(a.fecha.split('/').reverse().join('-'));
                              const dateB = new Date(b.fecha.split('/').reverse().join('-'));
                              return dateB - dateA;
                            });

                            const tooltipContenido = fechasPago.length > 0 ? (
                              <div>
                                <ul className="list-disc pl-4">
                                  {fechasPago.map((p) => (
                                    <li className={`mb-1 ${p.estado === "Pagado" ? "text-green-500" : "text-red-500"}`}>{p.fecha} - S/. {p.monto.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({p.estado})</li>
                                  ))}
                                </ul>
                              </div>
                            ) : (
                              <div>
                                <span>No hay fechas de pago</span>
                              </div>
                            );

                          return (
                            <Tooltip 
                              content={tooltipContenido}
                              allowHTML={true}
                              maxWidth={400}
                            >
                              <div className="flex items-center justify-center cursor-pointer">
                                <FaCalendarDays className="text-black" />
                              </div>
                            </Tooltip>
                            // <div>
                            //   <ul className="list-disc pl-4">
                            //     {fechasPago.map((p) => (
                            //       <li className={`mb-1 ${p.estado === "Pagado" ? "text-green-500" : "text-red-500"}`}>{p.fecha} - S/. {p.monto.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({p.estado})</li>
                            //     ))}
                            //   </ul>
                            // </div>
                          );
                        })()}
                      </td>
                      <td className={`px-4 py-2 whitespace-nowrap text-center border border-gray-300 font-bold ${verifyPayments.length > 0 ? "text-green-500 bg-green-50" : "text-gray-200"}`}>
                        {verifyPayments.length > 0 ? (verifyPayments.length + " pago(s) - " + verifyAmountPayments.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))  : "-"}
                      </td>
                      <td className={`px-4 py-2 whitespace-nowrap text-center border border-gray-300 font-bold ${verifyNonPayments.length > 0 ? "text-red-500 bg-red-100" : "text-gray-200"}`}>
                        {verifyNonPayments.length > 0 ? (verifyNonPayments.length + " pago(s) - " + verifyAmountNonPayments.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : "-"}
                      </td>
                      <td className={`px-4 py-2 whitespace-nowrap text-center border border-gray-300 font-bold ${owes ? "text-red-500 bg-red-100" : "text-green-500 bg-green-50"}`}>
                        {owes ? "Debe" : "Pagado"}
                      </td>
                      <td className={`px-4 py-2 whitespace-nowrap text-center border border-gray-300 ${payed > 0 ? "text-blue-500" : "text-gray-200"}`}>
                        {payed > 0 ? "S/. " + payed.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                      </td>
                      <td className={`px-4 py-2 whitespace-nowrap text-center border border-gray-300 ${nonPayed > 0 ? "text-red-500" : "text-gray-200"}`}>
                        {nonPayed > 0 ? "S/. " + nonPayed.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center border border-gray-300">
                        {totalPayments > 0 ? "S/. " + (totalPayments).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center border border-gray-300">
                        {totalWithoutIGV > 0 ? "S/. " + totalWithoutIGV.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                      </td>
                      <td className={`px-4 py-2 whitespace-nowrap text-center border border-gray-300 ${igvTotal > 0 ? "text-black" : "text-gray-200"}`}>
                        {igvTotal > 0 ? "S/. " + igvTotal.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                      </td>



                      {serviciosVisibles.map((s) => (
                        <td key={s.id} className={`px-4 py-2 whitespace-nowrap text-center border border-gray-300 ${montosPorServicio[s.id] > 0 ? "text-black" : "text-gray-200"}`}>
                          {montosPorServicio[s.id] > 0 ? "S/. " + montosPorServicio[s.id].toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                        </td>
                      ))}
                      
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* Paginador */}
      <PaginationItem
        actualDocs={record.length}
        totalDocs={record.length}
        entity={"clientes"}
        totalPages={1}
        actualPage={1}
        handlePageChange={(e, v) => setPage(v)}
      />
    </div>
  );
};

export default IngresosMensuales; 